import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import apiClient from "../api/apiClient";
import { useAuth } from "../auth/AuthContext";
import { useLanguage } from "../i18n/LanguageContext";
import "./PaymentResult.css";

const SETTLED = ["paid", "failed", "expired", "refunded"];
const POLL_INTERVAL_MS = 3000;
const MAX_POLLS = 10;

/**
 * Where Whish returns the member after payment.
 *
 * The status endpoint re-checks with Whish on every call, so a member who gets
 * back before the server-to-server callback still leaves this page with their
 * access granted rather than being told to wait.
 */
const PaymentResult = () => {
  const { t } = useLanguage();
  const { user, loading: authLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const reference = searchParams.get("ref");

  const [transaction, setTransaction] = useState(null);
  const [state, setState] = useState("checking");
  const attempts = useRef(0);

  const check = useCallback(async () => {
    if (!reference) {
      setState("missing");
      return true;
    }

    try {
      const { data } = await apiClient.get(`/payments/${reference}/status`);
      const payment = data.data ?? null;
      setTransaction(payment);

      if (payment && SETTLED.includes(payment.status)) {
        setState(payment.status === "paid" ? "paid" : "failed");
        return true;
      }

      setState("pending");
      return false;
    } catch (error) {
      setState(error.response?.status === 404 ? "missing" : "error");
      return true;
    }
  }, [reference]);

  useEffect(() => {
    if (authLoading || !user) return undefined;

    let cancelled = false;
    let timer;

    const run = async () => {
      const done = await check();
      if (cancelled) return;

      attempts.current += 1;

      if (!done && attempts.current < MAX_POLLS) {
        timer = window.setTimeout(run, POLL_INTERVAL_MS);
      }
    };

    run();

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [authLoading, user, check]);

  const amount = transaction ? `${transaction.currency} ${transaction.amount}` : null;

  const view = {
    checking: {
      icon: "fa-solid fa-circle-notch fa-spin",
      tone: "is-pending",
      title: t("Confirming your payment..."),
      body: t("This only takes a moment."),
    },
    pending: {
      icon: "fa-solid fa-clock",
      tone: "is-pending",
      title: t("Your payment is still processing"),
      body: t("We have not had final confirmation from Whish yet. You can safely close this page — your access opens automatically as soon as the payment clears."),
    },
    paid: {
      icon: "fa-solid fa-circle-check",
      tone: "is-paid",
      title: t("Payment confirmed"),
      body: t("Your subscription is active. Everything it unlocks is ready for you now."),
    },
    failed: {
      icon: "fa-solid fa-circle-xmark",
      tone: "is-failed",
      title: t("Payment was not completed"),
      body: t("Nothing was charged. You can pick your package again whenever you are ready."),
    },
    missing: {
      icon: "fa-solid fa-circle-question",
      tone: "is-failed",
      title: t("We could not find that payment"),
      body: t("Check the link you followed, or pick your package again."),
    },
    error: {
      icon: "fa-solid fa-triangle-exclamation",
      tone: "is-failed",
      title: t("Could not check your payment"),
      body: t("Please refresh in a moment. If it keeps happening, contact the academy team."),
    },
  }[state];

  if (!authLoading && !user) {
    return (
      <div className="page-content hpa-payment-page">
        <div className="container">
          <div className="hpa-payment-card is-pending">
            <span className="hpa-payment-icon"><i className="fa-solid fa-lock" /></span>
            <h1>{t("Login Required")}</h1>
            <p>{t("Log in to see the result of your payment.")}</p>
            <Link to="/auth" className="hpa-payment-btn">{t("Login")}</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content hpa-payment-page">
      <div className="container">
        <div className={`hpa-payment-card ${view.tone}`} role="status" aria-live="polite">
          <span className="hpa-payment-icon"><i className={view.icon} /></span>
          <h1>{view.title}</h1>
          <p>{view.body}</p>

          {amount && (
            <dl className="hpa-payment-meta">
              <div><dt>{t("Amount")}</dt><dd>{amount}</dd></div>
              <div><dt>{t("Reference")}</dt><dd>{transaction.external_id}</dd></div>
            </dl>
          )}

          <div className="hpa-payment-actions">
            {state === "paid" && (
              <>
                <Link to="/libraries" className="hpa-payment-btn">{t("Start training")}</Link>
                <Link to="/my-program" className="hpa-payment-btn is-ghost">{t("My Program")}</Link>
              </>
            )}
            {(state === "failed" || state === "missing") && (
              <Link to="/#packages" className="hpa-payment-btn">{t("Back to packages")}</Link>
            )}
            {state === "pending" && (
              <Link to="/libraries" className="hpa-payment-btn is-ghost">{t("Explore the libraries")}</Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentResult;
